import { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';

import {
  TreeExplorer,
  type TreeNode,
  type TreeExplorerProps,
} from '../../../shared/components/TreeExplorer';
import {
  useGetJurisdictionsQuery,
  useLazyGetSubjectsQuery,
  useLazyGetRootsQuery,
  useLazyGetTreeQuery,
  type TreeChild,
} from '../../standardsWarehouse/standardsWarehouseApi';
import { useImportWarehouseStandardsMutation } from '../customStandardsApi';

import type { UUID } from '@/shared/types/customStandard';

export type WarehouseSelection = {
  kind: 'root' | 'parent' | 'child';
  id: number;
  label: string;
  includeChildren?: boolean;
};

export function WarehouseInsertDialog({
  open,
  ownerId,
  targetParentId,
  onClose,
  onInserted,
}: {
  open: boolean;
  ownerId: UUID;
  targetParentId: UUID | null;
  onClose: () => void;
  onInserted?: (createdIds: string[]) => void;
}) {
  // 1) browse data
  const { data: jurisdictions = [], isLoading: loadingJur } = useGetJurisdictionsQuery();
  const [fetchSubjects] = useLazyGetSubjectsQuery();
  const [fetchRoots] = useLazyGetRootsQuery();
  const [fetchTree] = useLazyGetTreeQuery();

  // 2) selection and controls
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [includeChildren, setIncludeChildren] = useState(true); // for parent/root selections
  const [selections, setSelections] = useState<WarehouseSelection[]>([]);

  const [importWarehouse, { isLoading: importing }] = useImportWarehouseStandardsMutation();
  const [importError, setImportError] = useState<string | null>(null);

  const rootNodes: TreeNode[] = useMemo(
    () =>
      jurisdictions.map((j) => ({
        id: `jur-${j.id}`,
        label: j.title,
        type: 'jurisdiction',
        hasChildren: true,
        metadata: j,
      })),
    [jurisdictions]
  );

  type LoadChildren = NonNullable<TreeExplorerProps['onLoadChildren']>;
  const loadChildren = useCallback<LoadChildren>(
    async (node) => {
      const rawId = node.id.split('-')[1];
      try {
        switch (node.type) {
          case 'jurisdiction': {
            const res = await fetchSubjects(parseInt(rawId, 10)).unwrap();
            return res.map<TreeNode>((s) => ({
              id: `sub-${node.id}-${s.subject}`,
              label: s.subject,
              type: 'subject',
              hasChildren: true,
              metadata: { subject: s.subject },
            }));
          }
          case 'subject': {
            const parts = node.id.split('-');
            const jurId = parts[2];
            const res = await fetchRoots({
              jurisdictionId: parseInt(jurId, 10),
              subject: node.metadata?.subject,
            }).unwrap();
            return res.map<TreeNode>((r) => ({
              id: `root-${r.id}`,
              label: r.title,
              type: 'root',
              hasChildren: r.child_count > 0,
              metadata: { childCount: r.child_count },
            }));
          }
          case 'root': {
            const tree = await fetchTree(parseInt(rawId, 10)).unwrap();
            return tree.parents.map<TreeNode>((p) => ({
              id: `par-${p.id}`,
              label: p.description,
              type: 'parent',
              hasChildren: p.children.length > 0,
              metadata: { children: p.children, childCount: p.children.length },
            }));
          }
          case 'parent': {
            const kids = (node.metadata?.children || []) as TreeChild[];
            return kids.map((c) => ({
              id: `chi-${c.id}`,
              label: c.description,
              type: 'child',
              hasChildren: false,
              metadata: { indexed: c.indexed },
            }));
          }
          default:
            return [];
        }
      } catch (error) {
        console.error('Failed to load warehouse children:', error);
        throw error;
      }
    },
    [fetchSubjects, fetchRoots, fetchTree]
  );

  const handleSelectNode = (node: TreeNode) => {
    setSelectedNode(node);
  };

  const addSelection = () => {
    if (!selectedNode) return;
    const [kind, raw] = selectedNode.id.split('-') as [WarehouseSelection['kind'] | string, string];
    if (kind !== 'root' && kind !== 'parent' && kind !== 'child') return;
    const idNum = parseInt(raw, 10);
    if (Number.isNaN(idNum)) return;

    const exists = selections.some((s) => s.kind === kind && s.id === idNum);
    if (exists) return;

    setSelections((prev) => [
      ...prev,
      {
        kind,
        id: idNum,
        label: selectedNode.label,
        includeChildren: kind !== 'child' ? includeChildren : undefined,
      },
    ]);
  };

  /** Bulk-add all children of a parent node as individual child items */
  const addAllChildren = () => {
    if (!selectedNode) return;
    const kids = (selectedNode.metadata?.children || []) as TreeChild[];
    if (kids.length === 0) return;

    setSelections((prev) => {
      const next = [...prev];
      for (const c of kids) {
        const exists = next.some((s) => s.kind === 'child' && s.id === c.id);
        if (!exists) {
          next.push({ kind: 'child', id: c.id, label: c.description });
        }
      }
      return next;
    });
  };

  const removeSelection = (sel: WarehouseSelection) => {
    setSelections((prev) => prev.filter((s) => !(s.kind === sel.kind && s.id === sel.id)));
  };

  const handleImport = async () => {
    if (!ownerId) return;
    setImportError(null);
    try {
      const res = await importWarehouse({ ownerId, targetParentId, selections }).unwrap();
      const createdIds = res.map((r: { id: string }) => r.id);
      onInserted?.(createdIds);
      onClose();
      setSelections([]);
    } catch (e) {
      console.error('Import failed', e);
      setImportError(
        'Import failed. Some items may not have been created. Please check and try again.'
      );
    }
  };

  const canAdd =
    selectedNode &&
    (selectedNode.type === 'root' ||
      selectedNode.type === 'parent' ||
      selectedNode.type === 'child');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Insert from Standards Warehouse</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, minHeight: 480 }}>
          <Box sx={{ flex: 1 }}>
            <TreeExplorer
              rootNodes={rootNodes}
              isLoading={loadingJur}
              onLoadChildren={loadChildren}
              onNodeSelect={handleSelectNode}
              height={480}
              columnWidth={260}
              showMetadata={false}
            />
          </Box>

          <Divider flexItem orientation="vertical" />

          <Box sx={{ width: 340, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Selected warehouse item
            </Typography>
            <Box sx={{ mb: 2 }}>
              {selectedNode ? (
                <>
                  <Typography variant="body1">{selectedNode.label}</Typography>
                  {(selectedNode.type === 'root' || selectedNode.type === 'parent') && (
                    <>
                      <FormControlLabel
                        sx={{ mt: 1 }}
                        control={
                          <Checkbox
                            checked={includeChildren}
                            onChange={(e) => setIncludeChildren(e.target.checked)}
                          />
                        }
                        label="Include children"
                      />
                      {includeChildren && selectedNode.metadata?.childCount != null && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', ml: 4 }}
                        >
                          Will import {selectedNode.metadata.childCount} sub-standard
                          {selectedNode.metadata.childCount !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  )}
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" onClick={addSelection} disabled={!canAdd}>
                      Add to import list
                    </Button>
                    {selectedNode.type === 'parent' &&
                      ((selectedNode.metadata?.children as TreeChild[]) || []).length > 0 && (
                        <Button variant="text" size="small" onClick={addAllChildren}>
                          Add all {(selectedNode.metadata?.children as TreeChild[]).length} children
                          individually
                        </Button>
                      )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select an item in the tree
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Import list
              </Typography>
              {selections.length > 0 && (
                <Button size="small" color="inherit" onClick={() => setSelections([])}>
                  Clear all
                </Button>
              )}
            </Box>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {selections.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No items added yet
                </Typography>
              )}
              {selections.map((s) => (
                <Chip
                  key={`${s.kind}-${s.id}`}
                  label={`${s.label}${s.kind !== 'child' && s.includeChildren ? ' (+children)' : ''}`}
                  onDelete={() => removeSelection(s)}
                  color={s.kind === 'child' ? 'default' : 'primary'}
                  variant={s.kind === 'child' ? 'outlined' : 'filled'}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      {importError && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }} onClose={() => setImportError(null)}>
          {importError}
        </Alert>
      )}
      {importing && <LinearProgress sx={{ mx: 3 }} />}
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={selections.length === 0 || importing}
        >
          {importing ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Insert {selections.length > 0 ? `(${selections.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
